import { useEffect } from 'react'
import cx from 'classnames'

import { SetState, ViewType } from 'types'
import Styles from './Properties.module.scss'
import ViewPreferencesStyles from '../../../ViewPreferences.module.scss'

import SharedStyles from '../ViewControls.module.scss'
import { BackButton } from '../common/BackButton/BackButton'

import { useFinderContext } from 'screens/Finder/FinderContext'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useProperties } from 'hooks/useProperties'
import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'
import { PropertyService } from 'api/services/PropertyService'
import { ReactComponent as PlusIcon } from 'assets/icons/plus16.svg'
import { Button } from 'components/Button/Button'
import { Property, AttributeProperties, PROPERTY_TYPES_FOR_SHORTCUT } from 'api/clients/PropertyClient'
import { VisiblePadOptions, EditableProperty, PropertyVisibility } from './Properties'
import { PropertyRow } from './PropertyRow'
import { Tooltip } from 'components/Tooltip/Tooltip'

type Props = {
  setVisiblePad: SetState<VisiblePadOptions>
  setEditableProperty: SetState<EditableProperty | undefined>
  onBackButtonPress?: () => void
}

export const ViewProperties = ({ setVisiblePad, setEditableProperty, onBackButtonPress }: Props) => {
  const { filesView } = useFinderContext()
  const { isReadOnlyUser } = useHandbookReadOnlyUser()
  const { propertiesUsableInCurrentFolder, fromStore } = useProperties()
  const currentViewIsCalendar = filesView?.schema.default_view === ViewType.Calendar

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const workspace = useCurrentWorkspace()!

  useEffect(() => {
    PropertyService.getProperties(workspace.key, '')
  }, [workspace.key])

  const shortlistOfProperties = propertiesUsableInCurrentFolder.filter(
    ({ scope_project_id, default_for_articles, default_type }) =>
      default_for_articles || scope_project_id || (default_type && PROPERTY_TYPES_FOR_SHORTCUT.includes(default_type)),
  )

  const shownProperties = fromStore(filesView?.schema.properties || [])
  const shownWorkspaceProperties = shownProperties.filter(
    (shownProp) => !shortlistOfProperties.some((prop) => prop.id === shownProp.id),
  )
  const historicalProperties = fromStore(filesView?.schema.historical_properties || []).filter((property) => {
    return (
      !shortlistOfProperties.some((prop) => prop.id === property.id) &&
      !shownWorkspaceProperties.some((prop) => prop.id === property.id)
    )
  })

  const allProperties = [...shortlistOfProperties, ...shownWorkspaceProperties, ...historicalProperties]
  const allPropertiesForCalendar = allProperties.filter(
    (p) => !currentViewIsCalendar || p.id !== filesView?.schema.calendar_property_id,
  )
  const calendarProperty = allProperties.find((p) => p.id === filesView?.schema.calendar_property_id)

  const computeVisibility = (propertyId: number) => {
    if (shownProperties.some((prop) => prop.id === propertyId)) {
      return PropertyVisibility.Shown
    }

    return PropertyVisibility.Hidden
  }

  return (
    <Tooltip
      placement="right"
      label={`Edit access is required to modify the properties`}
      className={ViewPreferencesStyles.tooltipWidth}
      hide={!isReadOnlyUser}
    >
      <div className={cx(SharedStyles.control, Styles.propertiesControl)}>
        <div className={SharedStyles.title}>
          {onBackButtonPress && <BackButton onPress={onBackButtonPress} />}{' '}
          <span className={cx(SharedStyles.left, { [SharedStyles.noBackButton]: !onBackButtonPress })}>Properties</span>
        </div>
        <div className={SharedStyles.content}>
          <div>
            <PropertyRow
              property={AttributeProperties.name}
              visibility={PropertyVisibility.Shown}
              setVisiblePad={setVisiblePad}
              setEditableProperty={setEditableProperty}
              isDisabled={isReadOnlyUser}
            />
            {currentViewIsCalendar && calendarProperty && (
              <PropertyRow
                property={calendarProperty}
                visibility={PropertyVisibility.Shown}
                setVisiblePad={setVisiblePad}
                setEditableProperty={setEditableProperty}
                isDisabled={isReadOnlyUser}
              />
            )}
            {(currentViewIsCalendar ? allPropertiesForCalendar : allProperties).map((property: Property) => (
              <PropertyRow
                key={property.id}
                property={property}
                visibility={computeVisibility(property.id)}
                setVisiblePad={setVisiblePad}
                setEditableProperty={setEditableProperty}
                isDisabled={isReadOnlyUser}
              />
            ))}
          </div>
          <div className={cx(Styles.divider)}></div>
          <div className={Styles.flexContainer}>
            <Button
              className={cx(Styles.flexContainer, Styles.flexGrow, Styles.padding8, Styles.columnGap0)}
              variant="tertiary"
              size="lg"
              onPress={() => setVisiblePad('NewProperty')}
              isDisabled={isReadOnlyUser}
            >
              <PlusIcon className={cx(Styles.icon)} />
              <div className={Styles.label}>Add a property</div>
            </Button>
          </div>
        </div>
      </div>
    </Tooltip>
  )
}
